import config from "config";
import { classNames, useUrlState } from "utils";
import lodash from "lodash";
import numeral from "numeral";
import { FC } from "react";
import { Link } from "react-location";

export type SummaryTab = {
  name: string;
  href: string;
  activeStatuses?: string[];
  disabled?: boolean;
  hideCount?: boolean;
};

interface SummaryTabsProps {
  tabs: SummaryTab[];
  altTabs?: SummaryTab[];
  summary?: Record<string, number>;
  onSummaryTabChanged?: (tab: string) => void;
  label?: string;
  param?: string;
  enableAllStatusesTab?: boolean;
}

export const SummaryTabs: FC<SummaryTabsProps> = ({
  tabs,
  altTabs,
  summary,
  label,
  param = "status",
  enableAllStatusesTab = true,
}) => {
  const [tab] = useUrlState(param);

  return (
    <div className="block">
      <div className="border-b border-gray-200 bg-white px-6">
        <nav className="-mb-px flex space-x-4 items-center" aria-label="Tabs">
          {enableAllStatusesTab && (
            <Link
              search={(old) => ({
                ...old,
                [param]: undefined,
                page: config.constants.page,
                pageSize: config.constants.pageSize,
              })}
              className={classNames(
                !tab
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                "whitespace-nowrap flex py-4 px-1 border-b-2 font-medium text-sm",
              )}
              aria-current={!tab ? "page" : undefined}
            >
              All {label || ""}
              <span
                className={classNames(
                  !tab
                    ? "bg-primary-100 text-primary-600"
                    : "bg-gray-100 text-gray-900",
                  "hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block",
                )}
              >
                {numeral(
                  lodash.sum(lodash.values(lodash.omit(summary, "__typename"))),
                ).format("0,0")}
              </span>
            </Link>
          )}
          {tabs.map((_tab) => (
            <Link
              key={_tab.name}
              search={(old) => ({
                ...old,
                [param]: _tab.href,
                page: config.constants.page,
                pageSize: config.constants.pageSize,
              })}
              className={classNames(
                tab === _tab.href
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                "whitespace-nowrap flex py-4 px-1 border-b-2 font-medium text-sm",
              )}
              aria-current={tab === _tab.href ? "page" : undefined}
            >
              {_tab.name}
              {_tab.href && !_tab.hideCount ? (
                <span
                  className={classNames(
                    tab === _tab.href
                      ? "bg-primary-100 text-primary-600"
                      : "bg-gray-100 text-gray-900",
                    "hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block",
                  )}
                >
                  {numeral(summary?.[_tab?.href] || 0).format("0,0")}
                </span>
              ) : null}
            </Link>
          ))}
          {altTabs && (
            <>
              <div className="h-6 w-px bg-gray-300" />
              {altTabs?.map((_tab) => (
                <Link
                  key={_tab.name}
                  search={(old) => ({
                    ...old,
                    [param]: _tab.href,
                    page: config.constants.page,
                    pageSize: config.constants.pageSize,
                  })}
                  className={classNames(
                    tab === _tab.href
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                    "whitespace-nowrap flex py-4 px-1 border-b-2 font-medium text-sm",
                  )}
                  aria-current={tab === _tab.href ? "page" : undefined}
                >
                  {_tab.name}
                  {_tab.href &&
                  _tab.href !== "BulkInitiation" &&
                  !_tab.hideCount ? (
                    <span
                      className={classNames(
                        tab === _tab.href
                          ? "bg-primary-100 text-primary-600"
                          : "bg-gray-100 text-gray-900",
                        "hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block",
                      )}
                    >
                      {numeral(summary?.[_tab?.href] || 0).format("0,0")}
                    </span>
                  ) : null}
                </Link>
              ))}
            </>
          )}
        </nav>
      </div>
    </div>
  );
};

export default SummaryTabs;
